import { CloudUpload, Inbox, Keyboard, PackagePlus, Printer, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatMoney } from '@sense/shared';
import { useCart } from '../lib/cart';
import { useProductIndex, search, useProducts } from '../lib/catalog';
import { reprintLast } from '../lib/reprint';
import { syncNow } from '../lib/sync';
import { haptic, toast } from '../lib/toast';
import { NAV } from './nav';
import { IconTile, Kbd, type Tint } from './ui';
import { useUi } from './ui-store';
import { tr } from '../lib/i18n';

interface Cmd { id: string; label: string; hint?: string; icon: typeof Search; tint: Tint; run(): void }

export function CommandPalette() {
  const { palette, set } = useUi();
  const navigate = useNavigate();
  const products = useProducts();
  const index = useProductIndex();
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const close = () => set({ palette: false });

  useEffect(() => { if (palette) { setQ(''); setSel(0); setTimeout(() => input.current?.focus(), 10); } }, [palette]);

  const commands: Cmd[] = useMemo(() => [
    ...NAV.map((n) => ({ id: n.to, label: tr('Go to {0}', { 0: tr(n.label) }), hint: `Alt ${n.key.toUpperCase()}`, icon: n.icon, tint: n.tint, run: () => navigate(n.to) })),
    { id: 'new-product', label: tr('New product'), icon: PackagePlus, tint: 'amber', run: () => navigate('/inventory?new=1') },
    { id: 'receive', label: tr('Receive stock from supplier'), icon: Inbox, tint: 'blue', run: () => navigate('/receive') },
    { id: 'reprint', label: tr('Reprint last bill'), hint: 'F9', icon: Printer, tint: 'slate', run: () => void reprintLast() },
    { id: 'sync', label: tr('Sync now'), icon: CloudUpload, tint: 'blue', run: () => void syncNow().then(() => toast.ok(tr('Synced'))) },
    { id: 'help', label: tr('Keyboard shortcuts'), hint: '?', icon: Keyboard, tint: 'slate', run: () => set({ help: true }) },
  ], [navigate, set]);

  const results = useMemo(() => {
    const t = q.trim().toLowerCase();
    const cmds = t ? commands.filter((c) => c.label.toLowerCase().includes(t)) : commands;
    const prods = t.length >= 2 ? search(index, q, 8).matches.map((m): Cmd => ({
      id: 'p' + m.product.id, label: m.product.name, hint: formatMoney(m.product.price), icon: Search, tint: 'slate',
      run: () => { useCart.getState().add(m.product); haptic(); navigate('/billing'); toast.ok(tr('Added {0}', { 0: m.product.name })); },
    })) : [];
    return [...prods, ...cmds].slice(0, 14);
  }, [q, commands, index, navigate]);

  if (!palette) return null;
  const run = (c?: Cmd) => { if (!c) return; close(); c.run(); };
  return (
    <div className="anim-fade fixed inset-0 z-[55] flex items-start justify-center bg-ink/40 px-3 pt-[12vh]" onMouseDown={(e) => e.target === e.currentTarget && close()}>
      <div className="anim-sheet w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-lift" role="dialog" aria-label="Command palette">
        <label className="flex items-center gap-3 border-b border-line px-4">
          <Search size={20} className="text-muted" aria-hidden />
          <span className="sr-only">{tr('Search commands and products')}</span>
          <input ref={input} autoFocus value={q} onChange={(e) => { setQ(e.target.value); setSel(0); }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(s + 1, results.length - 1)); }
              else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
              else if (e.key === 'Enter') { e.preventDefault(); run(results[sel]); }
              else if (e.key === 'Escape') { e.preventDefault(); close(); }
            }}
            placeholder={tr('Type a page, action or product…')} className="h-14 flex-1 bg-transparent text-base outline-none" />
          <Kbd>{tr('Esc')}</Kbd>
        </label>
        <ul className="max-h-[55vh] overflow-y-auto p-2" role="listbox">
          {results.map((c, i) => (
            <li key={c.id} role="option" aria-selected={i === sel}>
              <button onMouseEnter={() => setSel(i)} onClick={() => run(c)}
                className={`flex w-full items-center gap-3 rounded-xl px-2 py-1.5 text-left text-base font-bold ${i === sel ? 'bg-forest-50' : ''}`}>
                <IconTile icon={c.icon} tint={c.tint} size={34} />
                <span className="flex-1 truncate">{c.label}</span>
                {c.hint && <span className="num text-xs text-muted">{c.hint}</span>}
              </button>
            </li>
          ))}
          {!results.length && <li className="px-3 py-6 text-center text-sm text-muted">{tr('Nothing matches “{0}”.', { 0: q })}</li>}
        </ul>
      </div>
    </div>
  );
}
